/**
 * `pdapp.links.page(page, { query, hash })` — the address of one of an app's OWN
 * pages, for wherever the app runs right now (a QR code: "open the remote on your
 * phone"). The ONE validator, shared by the SDK (which checks before it asks) and
 * Papan's host (which re-checks what an untrusted frame sent). Dependency-free
 * (only `./served-file`, itself import-free): the SDK bundles it.
 *
 * The address grants nothing — whoever opens it goes through the normal access
 * check (the app's link, or their own access in Papan).
 *
 * `page` is a path RELATIVE to the app folder, written as a file name, not a URL:
 *   - no leading `/` (`remote.html`, `pages/remote.html`) and no `?` / `#` (use
 *     `query` / `hash`);
 *   - no empty segment (`a//b`, a trailing `/`), no `.` / `..`, no `\`, no control
 *     characters, no scheme (`https:…`, `javascript:…`);
 *   - no segment starting with `_` or `.` — those are never served to a browser
 *     (`_scripts/`, `.config/`), so a link to one could only fail.
 * Each segment is percent-encoded as given (a page named `100%.html` stays that name).
 *
 * `query` is a plain object of string values, none of Papan's own keys
 * (`PAPAN_RESERVED_QUERY_KEYS`, `__ppn_…`); `hash` a string (one leading `#` is
 * dropped).
 */

import { RAW_READ_QUERY_PARAM } from './served-file.js'

export type AppPageLinkReach = 'link' | 'access'

export interface AppPageLink {
  url: string
  /** `'link'` — anyone the app's link lets in can open it. `'access'` — only people
   *  who already have access to the app in Papan. */
  reach: AppPageLinkReach
}

export interface AppPageLinkOptions {
  query?: Record<string, string>
  hash?: string
}

export type AppPageLinkProblem = 'invalid_page' | 'invalid_query' | 'invalid_hash'

/** A validated request: `path` per-segment encoded (no leading `/`), `search` `''`
 *  or `?…` (encoded), `hash` without its `#` (raw; the URL setter encodes it). */
export interface AppPageParts {
  path: string
  search: string
  hash: string
}

export type AppPagePartsResult =
  | { ok: true; parts: AppPageParts }
  | { ok: false; problem: AppPageLinkProblem; message: string }

const MAX_PAGE_CHARS = 1024
const MAX_QUERY_KEYS = 100
const MAX_QUERY_CHARS = 4096
const MAX_HASH_CHARS = 2048
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/
const SCHEME_RE = /^[A-Za-z][A-Za-z0-9+.-]*:/

function bad(problem: AppPageLinkProblem, message: string): AppPagePartsResult {
  return { ok: false, problem, message: `pdapp.links.page(): ${message}` }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const proto = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

/** Validate a page request. Never throws. */
export function appPageParts(page: unknown, opts?: unknown): AppPagePartsResult {
  if (typeof page !== 'string' || page.length === 0) return bad('invalid_page', 'the page must be a non-empty path inside the app, e.g. "remote.html"')
  if (page.length > MAX_PAGE_CHARS) return bad('invalid_page', `the page path is longer than ${MAX_PAGE_CHARS} characters`)
  if (page.startsWith('/')) return bad('invalid_page', 'write the page relative to the app folder, without a leading "/" ("remote.html")')
  if (CONTROL_RE.test(page) || page.includes('\\')) return bad('invalid_page', 'the page path holds a control character or a backslash')
  if (page.includes('?') || page.includes('#')) return bad('invalid_page', 'pass the query and hash as options ({ query, hash }), not in the page path')
  if (SCHEME_RE.test(page)) return bad('invalid_page', 'the page must be a path inside the app, not a URL')
  const segments = page.split('/')
  for (const seg of segments) {
    if (seg === '') return bad('invalid_page', 'the page path has an empty segment ("//" or a trailing "/")')
    if (seg === '.' || seg === '..') return bad('invalid_page', 'the page path may not contain "." or ".." segments')
    if (seg.startsWith('_') || seg.startsWith('.')) return bad('invalid_page', `"${seg}" starts with "_" or "." — such paths are never served to a browser`)
  }
  const path = segments.map(encodeURIComponent).join('/')

  if (opts !== undefined && opts !== null && !isPlainObject(opts)) return bad('invalid_query', 'the options must be an object ({ query, hash })')
  const o = (opts ?? {}) as Record<string, unknown>

  let search = ''
  if (o.query !== undefined && o.query !== null) {
    if (!isPlainObject(o.query)) return bad('invalid_query', 'query must be a plain object of string values')
    const entries = Object.entries(o.query)
    if (entries.length > MAX_QUERY_KEYS) return bad('invalid_query', `query has more than ${MAX_QUERY_KEYS} keys`)
    const params = new URLSearchParams()
    for (const [k, v] of entries) {
      if (k === '') return bad('invalid_query', 'query keys must be non-empty')
      if (isPapanReservedQueryKey(k)) return bad('invalid_query', `query key "${k}" is Papan's own and never reaches the app — use another name`)
      if (typeof v !== 'string') return bad('invalid_query', `query value "${k}" must be a string`)
      params.append(k, v)
    }
    const qs = params.toString()
    if (qs.length > MAX_QUERY_CHARS) return bad('invalid_query', `the query is longer than ${MAX_QUERY_CHARS} characters`)
    search = qs ? `?${qs}` : ''
  }

  let hash = ''
  if (o.hash !== undefined && o.hash !== null) {
    if (typeof o.hash !== 'string') return bad('invalid_hash', 'hash must be a string')
    hash = o.hash.startsWith('#') ? o.hash.slice(1) : o.hash
    if (hash.length > MAX_HASH_CHARS) return bad('invalid_hash', `the hash is longer than ${MAX_HASH_CHARS} characters`)
    if (CONTROL_RE.test(hash)) return bad('invalid_hash', 'the hash holds a control character')
  }
  return { ok: true, parts: { path, search, hash } }
}

/**
 * The page's absolute URL under `base` — an origin, optionally with the path the
 * app root sits at (`https://papan.ai/t/acme/app/F1`), no trailing `/`. Throws a
 * `TypeError` only for a base that is not an absolute http(s) URL.
 */
export function appPageUrl(base: string, parts: AppPageParts): string {
  const root = new URL(base)
  if (root.protocol !== 'https:' && root.protocol !== 'http:') throw new TypeError('appPageUrl: base must be an http(s) URL')
  const prefix = root.pathname.replace(/\/+$/, '')
  const u = new URL(`${root.origin}${prefix}/${parts.path}`)
  u.search = parts.search
  u.hash = parts.hash
  return u.href
}

/**
 * Papan's OWN query keys — the ONE list. They can sit on an SPA URL or an app page's
 * serve URL but are never the app's, so they never reach an app frame
 * (`appOwnUrlSuffix` drops them) and `pdapp.links.page()` refuses them
 * (`appPageParts` → `invalid_query`):
 *   - `orgEmail` picks the request's identity, `devlog` turns on the dev remote log,
 *     `_t` is the frame's reload cache-buster (the SPA / the frame host);
 *   - `download` (forced attachment), `papanRaw` (`RAW_READ_QUERY_PARAM`, the inert raw
 *     bytes) and `papanProbe` (the share view's byte probe) are read by the `/s/` serve
 *     route off ANY served page URL (`features/files/api/preview-serve.ts` — a test pins
 *     every key it reads to this list);
 *   - every `__ppn_…` key is the app-link router's own (`__ppn_sid`, `__ppn_ho`, …).
 */
export const PAPAN_RESERVED_QUERY_KEYS: readonly string[] = Object.freeze(['orgEmail', 'devlog', '_t', 'download', RAW_READ_QUERY_PARAM, 'papanProbe'])
const RESERVED_SET: ReadonlySet<string> = new Set(PAPAN_RESERVED_QUERY_KEYS)
/** The app-link router's own query-key prefix. */
export const PAPAN_ROUTER_QUERY_PREFIX = '__ppn_'

/** Is `key` (decoded) one of Papan's own query keys? */
export function isPapanReservedQueryKey(key: string): boolean {
  return RESERVED_SET.has(key) || key.startsWith(PAPAN_ROUTER_QUERY_PREFIX)
}

/**
 * The APP's own `?query#hash` from an SPA deep link (`/t/<t>/app/<ref>/<page>?…#…`),
 * ready to append to the app frame's page URL: `''`, or starting `?` / `#`. Papan's
 * own params are dropped; everything else is passed through as written.
 */
export function appOwnUrlSuffix(search: string | null | undefined, hash: string | null | undefined): string {
  let query = ''
  const raw = (search ?? '').replace(/^\?/, '')
  if (raw) {
    const kept = raw.split('&').filter((pair) => {
      if (!pair) return false
      const eq = pair.indexOf('=')
      let key = eq >= 0 ? pair.slice(0, eq) : pair
      try { key = decodeURIComponent(key.replace(/\+/g, ' ')) } catch { /* keep as written */ }
      return !isPapanReservedQueryKey(key)
    })
    query = kept.length ? `?${kept.join('&')}` : ''
  }
  const h = (hash ?? '').replace(/^#/, '')
  return `${query}${h ? `#${h}` : ''}`
}
